<script lang="ts" setup>
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { PropertyType } from '@/types';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import ValuationFormModal from '@/components/property-details-page/ValuationFormModal/ValuationFormModal.vue';
import AgentRecommendations from '@/components/property-valuation-page/AgentRecommendations/AgentRecommendations.vue';
import CodeInput from '@/components/property-valuation-page/CodeInput';
import ContactData from '@/components/property-valuation-page/ContactData';
import FooterBanner from '@/components/property-valuation-page/Footer/FooterBanner.vue';
import FunnelAbandonmentModal from '@/components/property-valuation-page/FunnelAbandonmentModal/FunnelAbandonmentModal.vue';
import OfferType from '@/components/property-valuation-page/OfferType';
import PhoneNumber from '@/components/property-valuation-page/PhoneNumber';
import PostalCode from '@/components/property-valuation-page/PostalCode/PostalCode.vue';
import PropertyDetails from '@/components/property-valuation-page/PropertyDetails/PropertyDetails.vue';
import PropertyOwnership from '@/components/property-valuation-page/PropertyOwnership/PropertyOwnership.vue';
import PropertyTypeComponent from '@/components/property-valuation-page/PropertyType';
import Renovation from '@/components/property-valuation-page/Renovation';
import RenovationYear from '@/components/property-valuation-page/RenovationYear';
import StreetAndNumber from '@/components/property-valuation-page/StreetAndNumber/StreetAndNumber.vue';
import WhenToSell from '@/components/property-valuation-page/WhenToSell';
import { useFunnelAbandonmentGuard } from '@/composables/useFunnelAbandonmentGuard';
import { LogoIs24 } from '@smg-real-estate/domus';

const propertyValuationStore = usePropertyValuationStore();
const { step: formStep, isIv24FunnelActive } = storeToRefs(propertyValuationStore);
const { t, locale } = useI18n();
const route = useRoute();

const {
  isModalVisible: isAbandonmentModalVisible,
  isAuthModalVisible: isAbandonmentAuthModalVisible,
  isCreatingProperty: isAbandonmentPropertyCreating,
  formattedAddress: abandonmentFormattedAddress,
  confirmLeave: confirmFunnelAbandonmentLeave,
  cancelLeave: cancelFunnelAbandonmentLeave,
  closeAuthModal: closeFunnelAbandonmentAuthModal,
  confirmAuthModal: confirmFunnelAbandonmentAuthModal,
} = useFunnelAbandonmentGuard();

const steps = [
  PropertyTypeComponent,
  OfferType,
  PostalCode,
  StreetAndNumber,
  PropertyDetails,
  Renovation,
  RenovationYear,
  PropertyOwnership,
  WhenToSell,
  ContactData,
  PhoneNumber,
  AgentRecommendations,
  CodeInput,
];

const initData = (propertyType: PropertyType) => {
  propertyValuationStore.initData(propertyType);
};

const resetForm = () => {
  propertyValuationStore.resetForm();
};

const setTotalSteps = (totalSteps: number) => {
  propertyValuationStore.setTotalSteps(totalSteps);
};

const shouldDisplayFooterBanner = computed((): boolean => {
  if (isIv24FunnelActive.value) {
    return false;
  }

  return formStep.value == 0 || formStep.value == 1;
});

onMounted(() => {
  const propertyType = route.query?.pt;
  if (propertyType === PropertyType.House || propertyType === PropertyType.Apartment) {
    initData(propertyType as PropertyType);
  }
});

onBeforeMount(() => {
  setTotalSteps(steps.length);
});

onBeforeUnmount(() => {
  resetForm();
});

useHead(() => ({
  title: t(`metaInfo.valuationFunnel.title`),
  htmlAttrs: {
    lang: locale.value,
  },
}));
</script>

<template>
  <div :class="[$style.wrapper, 'layout-container']">
    <component :is="steps[formStep]" :key="formStep" />
    <div v-if="isIv24FunnelActive" class="powered-by">
      <span>powered by</span>
      <LogoIs24 class="logo" />
    </div>
    <FooterBanner v-if="shouldDisplayFooterBanner" />
    <FunnelAbandonmentModal
      v-model="isAbandonmentModalVisible"
      :formattedAddress="abandonmentFormattedAddress"
      :isCreatingProperty="isAbandonmentPropertyCreating"
      @confirm="confirmFunnelAbandonmentLeave"
      @cancel="cancelFunnelAbandonmentLeave"
    />
    <ValuationFormModal
      :showModal="isAbandonmentAuthModalVisible"
      @onClose="closeFunnelAbandonmentAuthModal"
      @onConfirm="confirmFunnelAbandonmentAuthModal"
    />
  </div>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.wrapper[class*='layout-container'] {
  position: relative;
  padding-top: $space-150;
}
</style>

<style lang="scss">
@use '@/assets/variables' as *;

.powered-by {
  display: flex;
  flex-wrap: wrap;
  gap: $space-50;
  align-items: center;
  justify-content: center;
  margin-block: $space-300 $space-600;

  span {
    @include caption;
  }

  .logo {
    display: inline-block;
    width: $space-800;
  }
}
</style>
